import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { HemaHeadingWithSubText, HemaLabel, HemaValue, Button } from "../../utils";
import LoginInput from "../../utils/FormElements/loginInput";
import { showErrorReducer } from "../../Store/reducers/uiSettings";
import { VerifyMfaAction, ResendMfaAction } from "../../Actions/auth";

const HELP_DESK_EMAIL =
  process.env.REACT_APP_HELPDESK_EMAIL || "HelpDesk@hematogenix.com";

const MfaForm = ({ email, onVerified, onCancel, initialResendIn = 0 }) => {
  const dispatch = useDispatch();
  const [resendIn, setResendIn] = useState(initialResendIn);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);

  const MAX_ATTEMPTS = 5;

  useEffect(() => {
    setResendIn(initialResendIn || 0);
  }, [initialResendIn]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  return (
    <div className="max-w-full lg:max-w-[450px]">
      <Formik
        initialValues={{ code: "" }}
        validate={(values) => {
          const errors = {};
          if (!/^\d{6}$/.test(values.code)) errors.code = "Enter the 6-digit code";
          dispatch(showErrorReducer(""));
          return errors;
        }}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          if (locked) return;

          const res = await VerifyMfaAction({ email, code: values.code });

          if (res?.status === 200 && res?.data?.token) {
            setSubmitting(false);
            setAttempts(0);
            onVerified?.(res.data.token);
            return;
          }

          const next = attempts + 1;
          setAttempts(next);
          if (next >= MAX_ATTEMPTS) {
            setLocked(true);
            dispatch(showErrorReducer(`Too many attempts. Please email ${HELP_DESK_EMAIL}`));
          } else {
            dispatch(showErrorReducer(res?.message || "Verification failed"));
          }

          setSubmitting(false);
          resetForm();
        }}
      >
        {({ values, errors, touched, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <HemaHeadingWithSubText heading="Two-Step Verification" />

            {!!email && (
              <div className="mb-4">
                <div className="mt-1 mb-3">
                  <HemaValue text={`Code sent to ${email}`} className="text-[#6B6B6B]" />
                </div>

                <div className="flex items-center justify-between">
                  <HemaValue
                    text={
                      resendIn > 0
                        ? `You can resend a new code in ${resendIn}s`
                        : "Didn't get the code?"
                    }
                    className="text-[#6B6B6B]"
                  />
                  <Button
                    type="button"
                    text="Resend"
                    bg="bg-transparent"
                    border="border-primary1"
                    color="text-primary1"
                    className="h-[40px] px-4 font-semibold"
                    disabled={locked || resendIn > 0 || isSubmitting}
                    cta={async () => {
                      if (locked || resendIn > 0 || isSubmitting) return;
                      try {
                        const r = await ResendMfaAction({ email });
                        if (r?.status === 200 || r?.status === 204) {
                          setResendIn(60);
                        } else {
                          setResendIn(60);
                        }
                      } catch (e) {
                        const seconds = 60;
                        setResendIn(seconds);
                      }
                    }}
                  />
                </div>
              </div>
            )}
            {!(locked || isSubmitting) && (
              <div className="flex flex-col mb-[24px]">
                <HemaLabel text="6-Digit Code" textSize="text-[16px]" />
                <LoginInput
                  type="text"
                  name="code"
                  placeholder="Enter the 6-digit code"
                  value={values.code}
                  onChange={(e) => {
                    const only = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setFieldValue("code", only);
                  }}
                  onBlur={handleBlur}
                  setFieldValue={setFieldValue}
                />
                <p className="error m-0 text-[#F54C3B]">
                  {errors.code && touched.code && errors.code}
                </p>
              </div>
            )}
            {isSubmitting ? (
              <Button
                type="submit"
                bg="bg-primary1"
                className="w-full h-[48px] justify-center font-semibold"
                text={
                  <>
                    <Spinner animation="grow" size="sm" variant="light" />
                    <Spinner animation="grow" size="sm" variant="light" />
                    <Spinner animation="grow" size="sm" variant="light" />
                  </>
                }
              />
            ) : (
              <Button
                text="Verify Code"
                type="submit"
                bg="bg-primary1"
                border="border-primary1"
                color="text-white"
                className="w-full h-[48px] justify-center font-semibold"
                disabled={locked || isSubmitting || !/^\d{6}$/.test(values.code)}
              />
            )}

            {!isSubmitting && (
              <div className="mt-[22px]">
                <Button
                  text="Back to Log In"
                  type="button"
                  bg="bg-primary1"
                  border="border-primary1"
                  color="text-white"
                  className="w-full h-[48px] justify-center font-semibold"
                  cta={() => onCancel?.()}
                />
              </div>
            )}

            <div className="mt-4 text-center text-sm text-[#6B6B6B]">
              If you are having trouble accessing your account, please contact us at{" "}
              <a
                href={`mailto:${HELP_DESK_EMAIL}`}
                className="text-primary1 font-semibold underline"
              >
                {HELP_DESK_EMAIL}
              </a>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default MfaForm;
